import { format, parseISO } from "date-fns";
import { ja } from "date-fns/locale";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  PageContainer,
  PageContent,
  PageHeader,
} from "../components/layout/PageHeader";
import { Alert } from "../components/ui/Alert";
import { Button } from "../components/ui/Button";
import { Card, CardContent, CardHeader } from "../components/ui/Card";
import { ConfirmModal } from "../components/ui/Modal";
import { ReservationListSkeleton } from "../components/ui/Skeleton";
import {
  type RepeatWithSeat,
  type Reservation,
  useCancelRecurringReservation,
  useCancelReservation,
  useMyRepeats,
  useMyReservations,
} from "../hooks/api/useReservations";
import { useToastStore } from "../stores/toastStore";
import { RecurringReservationsSection } from "./my-reservations/RecurringReservationsSection";
import { UpcomingReservationsSection } from "./my-reservations/UpcomingReservationsSection";

interface CancelTarget {
  date: string;
  seatId: number;
  seatLabel: string;
}

interface RecurringCancelTarget {
  repeatId: string;
  seatLabel: string;
}

function getErrorMessage(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback;
}

function ReservationContent({
  reservations,
  repeats,
  isLoading,
  hasError,
  onCancel,
  onCancelAll,
}: {
  reservations: Reservation[] | undefined;
  repeats: RepeatWithSeat[] | undefined;
  isLoading: boolean;
  hasError: boolean;
  onCancel: (reservation: Reservation) => void;
  onCancelAll: (repeat: RepeatWithSeat) => void;
}) {
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <Card>
        <CardHeader title="予約一覧" />
        <CardContent>
          <ReservationListSkeleton />
        </CardContent>
      </Card>
    );
  }

  if (hasError) {
    return (
      <Card>
        <CardHeader title="予約一覧" />
        <CardContent>
          <Alert variant="error">
            予約の取得に失敗しました。再読み込みしてください。
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const singleReservations = reservations?.filter((r) => !r.repeatId) ?? [];
  const hasSingleReservations = singleReservations.length > 0;
  const hasRepeats = repeats && repeats.length > 0;

  if (!(hasSingleReservations || hasRepeats)) {
    return (
      <Card>
        <CardHeader title="予約一覧" />
        <CardContent>
          <div className="flex flex-col items-center gap-4 py-12 text-gray-500">
            <svg
              className="h-12 w-12 text-gray-300"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"
              />
            </svg>
            <p>予約がありません</p>
            <Button
              variant="primary"
              size="md"
              onClick={() => navigate("/seats")}
            >
              座席マップから予約する
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {hasSingleReservations && (
        <Card>
          <CardHeader title="今後の予約" />
          <CardContent>
            <UpcomingReservationsSection
              reservations={singleReservations}
              onCancel={onCancel}
            />
          </CardContent>
        </Card>
      )}

      {hasRepeats && (
        <Card>
          <CardHeader title="リピート予約" />
          <CardContent>
            <RecurringReservationsSection
              repeats={repeats}
              onCancelAll={onCancelAll}
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export function MyReservationsPage() {
  const todayStr = useMemo(() => format(new Date(), "yyyy-MM-dd"), []);
  const {
    data: reservations,
    isLoading: isLoadingReservations,
    error: reservationsError,
  } = useMyReservations(todayStr);
  const {
    data: repeats,
    isLoading: isLoadingRepeats,
    error: repeatsError,
  } = useMyRepeats();
  const cancelReservation = useCancelReservation();
  const cancelRecurring = useCancelRecurringReservation();
  const addToast = useToastStore((s) => s.addToast);

  const [cancelTarget, setCancelTarget] = useState<CancelTarget | null>(null);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [recurringCancelTarget, setRecurringCancelTarget] =
    useState<RecurringCancelTarget | null>(null);
  const [recurringCancelError, setRecurringCancelError] = useState<
    string | null
  >(null);

  const handleCancelClick = (reservation: Reservation) => {
    setCancelTarget({
      date: reservation.date,
      seatId: reservation.seatId,
      seatLabel: `座席 ${reservation.seat?.label ?? reservation.seatId}`,
    });
  };

  const handleCancelConfirm = async () => {
    if (!cancelTarget) {
      return;
    }

    setCancelError(null);
    try {
      await cancelReservation.mutateAsync({
        date: cancelTarget.date,
        seatId: cancelTarget.seatId,
      });
      addToast(`${cancelTarget.seatLabel}の予約をキャンセルしました`);
      setCancelTarget(null);
    } catch (err) {
      setCancelError(getErrorMessage(err, "キャンセルに失敗しました"));
    }
  };

  const handleCancelClose = () => {
    setCancelTarget(null);
    setCancelError(null);
  };

  const handleRecurringCancelClick = (repeat: RepeatWithSeat) => {
    setRecurringCancelTarget({
      repeatId: repeat.repeatId,
      seatLabel: `座席 ${repeat.seat?.label ?? repeat.seatId}`,
    });
  };

  const handleRecurringCancelConfirm = async () => {
    if (!recurringCancelTarget) {
      return;
    }

    setRecurringCancelError(null);
    try {
      await cancelRecurring.mutateAsync({
        repeatId: recurringCancelTarget.repeatId,
      });
      addToast(
        `${recurringCancelTarget.seatLabel}の繰り返し予約をすべてキャンセルしました`
      );
      setRecurringCancelTarget(null);
    } catch (err) {
      setRecurringCancelError(
        getErrorMessage(err, "繰り返し予約のキャンセルに失敗しました")
      );
    }
  };

  const handleRecurringCancelClose = () => {
    setRecurringCancelTarget(null);
    setRecurringCancelError(null);
  };

  return (
    <PageContainer>
      <PageHeader />
      <PageContent>
        <h1 className="sr-only">予約一覧</h1>
        {cancelError && (
          <Alert
            variant="error"
            className="mb-4"
            onClose={() => setCancelError(null)}
          >
            {cancelError}
          </Alert>
        )}
        <ReservationContent
          reservations={reservations}
          repeats={repeats}
          isLoading={isLoadingReservations || isLoadingRepeats}
          hasError={!!(reservationsError || repeatsError)}
          onCancel={handleCancelClick}
          onCancelAll={handleRecurringCancelClick}
        />
      </PageContent>

      <ConfirmModal
        isOpen={!!cancelTarget}
        onClose={handleCancelClose}
        onConfirm={handleCancelConfirm}
        title="予約のキャンセル"
        message={
          cancelTarget
            ? `${format(parseISO(cancelTarget.date), "M月d日（E）", { locale: ja })}の${cancelTarget.seatLabel}の予約をキャンセルしますか？`
            : ""
        }
        confirmText="キャンセルする"
        cancelText="閉じる"
        variant="danger"
        isLoading={cancelReservation.isPending}
      >
        {cancelError && (
          <Alert variant="error" className="mt-3">
            {cancelError}
          </Alert>
        )}
      </ConfirmModal>

      <ConfirmModal
        isOpen={!!recurringCancelTarget}
        onClose={handleRecurringCancelClose}
        onConfirm={handleRecurringCancelConfirm}
        title="繰り返し予約の一括キャンセル"
        message={
          recurringCancelTarget
            ? `${recurringCancelTarget.seatLabel}の繰り返し予約をすべてキャンセルしますか？今後の予約もすべて削除されます。`
            : ""
        }
        confirmText="すべてキャンセル"
        cancelText="閉じる"
        variant="danger"
        isLoading={cancelRecurring.isPending}
      >
        {recurringCancelError && (
          <Alert variant="error" className="mt-3">
            {recurringCancelError}
          </Alert>
        )}
      </ConfirmModal>
    </PageContainer>
  );
}
